import dayjs from "dayjs";
import * as yup from "yup";
import { KeyValueMap } from "../../redux/slices/appState/models";
import {
  getTodayDate,
  getTomorrowDate,
  getValueOrBlank,
  getYesterdayDate,
  isEmpty,
  removeSpecialCharacters,
} from "../utils";
import { DATE_FORMAT } from "./common";
import {
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
  REGEX_ONLY_NUMBER,
  REGEX_VALID_EMAIL,
} from "./validations";

export const greaterThanZeroValidation = (message: string) =>
  yup.string().test("is-greater-than-zero", message, (value) => {
    if (isEmpty(value)) return true; // allow empty values (use required separately)

    const cleanValue = removeSpecialCharacters(value ?? "");
    if (!REGEX_ONLY_NUMBER.test(cleanValue)) return true;

    const numberValue = Number(cleanValue);
    return !isNaN(numberValue) && numberValue > 0;
  });

export const lessThanValueValidation = (max: number, message: string) =>
  yup.string().test(`is-less-than-${max}`, message, (value) => {
    if (isEmpty(value)) return true;
    const numberValue = Number(value);
    return !isNaN(numberValue) && numberValue <= max;
  });

export const validEmailValidation = (message: string) =>
  yup
    .string()
    .test(
      "is-valid-email",
      message,
      (value) => !value || REGEX_VALID_EMAIL.test(value),
    );

export const alphabetOrSpecialCharNotAllowedValidation = (message: string) =>
  yup.string().matches(REGEX_ONLY_NUMBER, message);

export const validFieldValidation = (
  message: string,
  regex: RegExp = INPUT_LIMIT.ALPHA_NUMERIC_WITH_DASH_AND_SPACE,
) =>
  yup
    .string()
    .test("is-valid", message, (value) => !value || regex.test(value));

export const dateWithValidation = (
  isOptional: boolean,
  commonValidationMessages?: KeyValueMap,
  restrictPastDate?: boolean,
  restrictFutureDate?: boolean,
  userCountryDateFormat?: string,
  allowToday?: boolean,
) => {
  const baseSchema = yup.string().default("");

  let schema = isOptional
    ? baseSchema.notRequired().nullable()
    : baseSchema.required(
        getValueOrBlank(commonValidationMessages?.requiredValidation),
      );

  schema = schema.test(
    "is-valid-date",
    getValueOrBlank(commonValidationMessages?.invalidDate),
    (value) => {
      if (!value) return true;
      return dayjs(value, userCountryDateFormat).isValid();
    },
  );

  if (restrictPastDate) {
    schema = schema.test(
      "is-after-today",
      getValueOrBlank(commonValidationMessages?.pleaseChooseAFutureDate),
      (value) => {
        if (!value) return true;
        return !checkDatePastValidation(
          dayjs(value),
          userCountryDateFormat,
          allowToday,
        );
      },
    );
  }

  if (restrictFutureDate) {
    schema = schema.test(
      "is-before-today",
      getValueOrBlank(commonValidationMessages?.pleaseChooseAPastDate),
      (value) => {
        if (!value) return true;
        return !checkDateFutureValidation(
          dayjs(value),
          userCountryDateFormat,
          allowToday,
        );
      },
    );
  }
  return schema;
};

export const checkDatePastValidation = (
  compareDate: dayjs.Dayjs,
  userCountryDateFormat?: string,
  allowToday?: boolean,
) => {
  if (!compareDate) return true;

  const compareDateStr = compareDate.format(
    userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN,
  );
  const finalStr = allowToday ? getTodayDate() : getTomorrowDate();
  const formattedFinalStr = finalStr.format(
    userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN,
  );

  return dayjs(compareDateStr).isBefore(formattedFinalStr);
};

export const checkDateFutureValidation = (
  compareDate: dayjs.Dayjs,
  userCountryDateFormat?: string,
  allowToday?: boolean,
) => {
  if (!compareDate) return true;

  const compareDateStr = compareDate.format(
    userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN,
  );

  const finalStr = allowToday ? getTodayDate() : getYesterdayDate();
  const formattedFinalStr = finalStr.format(
    userCountryDateFormat || DATE_FORMAT.YYYY_MM_DD_HYPHEN,
  );

  return dayjs(compareDateStr).isAfter(formattedFinalStr);
};

export const isNumberBetweenRangeValidation = (
  min: number,
  max: number,
  message: string,
) =>
  yup.string().test(`is-between-${min}-and-${max}`, message, (value) => {
    if (isEmpty(value)) return true; // Skip validation if the field is empty
    const numberValue = Number(value);
    return numberValue > min && numberValue <= max;
  });

export const symmetricalFieldTest = (
  fieldKey: string, // current field key
  otherFieldKey: string, // other field key to compare with
  message: string, // validation message
) => {
  return yup
    .string()
    .optional()
    .test(`${fieldKey}-symmetrical`, message, function (value) {
      const otherValue = this.parent[otherFieldKey];

      // ❌ if only one is filled and the other empty → error
      if ((!!value && !otherValue) || (!value && !!otherValue)) {
        return !!value; // false if current field is empty
      }

      // ✅ valid when both empty or both filled
      return true;
    });
};
export const notSameAsFieldTest = (
  fieldKey: string,
  otherFieldKey: string,
  message: string,
) =>
  yup
    .string()
    .optional()
    .test(
      `${fieldKey}-not-same-as-${otherFieldKey}`,
      message,
      function (value) {
        const otherValue = this.parent[otherFieldKey];
        if (!value || !otherValue) return true; // Only check if both have values
        return value.trim().toLowerCase() !== otherValue.trim().toLowerCase();
      },
    );

export const specialCharactersNotAllowedValidation = (
  requiredValidation: string,
  invalidCharactersErrorMessage: string,
) =>
  yup
    .string()
    .required(requiredValidation)
    .test(
      EXTENDED_SPECIAL_CHARS,
      invalidCharactersErrorMessage,
      (value) =>
        !value ||
        INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
    );

export const specialCharactersNotAllowedValidationForOptionalField = (
  invalidCharactersErrorMessage: string,
) =>
  yup
    .string()
    .optional()
    .test(
      EXTENDED_SPECIAL_CHARS,
      invalidCharactersErrorMessage,
      (value) =>
        !value ||
        INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value),
    );

export const checkValueWithValidationRegex = (value: string, regex: RegExp) =>
  !value || regex.test(value);

export const isRequiredFieldValidation = (
  isRequired: boolean,
  message: string,
) => {
  return isRequired
    ? yup.string().required(message)
    : yup.string().optional().nullable();
};